import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { User, Lock, Eye, EyeOff, Save, Shield, Calendar, Coins, Users, ExternalLink, Search, X, Mail, KeyRound, Loader2, Phone } from 'lucide-react';
import { api } from '@/store';

export default function ProfilePage() {
  const { user, checkAuth } = useAuthStore();
  const [username, setUsername] = useState(user?.username || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.two_factor_enabled === true);
  const [savingTwoFactor, setSavingTwoFactor] = useState(false);
  const [supportWhatsapp, setSupportWhatsapp] = useState(user?.support_whatsapp || '');
  const [savingSupport, setSavingSupport] = useState(false);
  
  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState('idle'); // idle, verify
  const [loadingEmail, setLoadingEmail] = useState(false);
  
  // Admin groups modal
  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');

  const isAdmin = user?.role === 'admin';
  const isMasterOrAdmin = user?.role === 'master' || user?.role === 'admin';
  const [netDisableConn, setNetDisableConn] = useState(false);
  const [netDisableRef, setNetDisableRef] = useState(false);
  useEffect(() => {
    if (!isMasterOrAdmin) return;
    api.get('/network/settings').then(r => {
      setNetDisableConn(!!r.data.disable_extra_connections);
      setNetDisableRef(!!r.data.disable_referral);
    }).catch(() => {});
  }, [isMasterOrAdmin]);
  const saveNet = async (patch) => {
    try { await api.put('/network/settings', patch); } catch (e) {}
  };

  const fetchGroupsWithInvite = async () => {
    setLoadingGroups(true);
    try {
      const response = await api.get('/admin/groups-with-invite');
      setGroups(response.data);
    } catch (error) {
      toast.error('Erro ao carregar grupos');
    } finally {
      setLoadingGroups(false);
    }
  };

  const openGroupsModal = () => {
    setGroupsModalOpen(true);
    fetchGroupsWithInvite();
  };

  const filteredGroups = groups.filter(g => 
    g.name?.toLowerCase().includes(groupSearch.toLowerCase()) ||
    g.owner_username?.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error('Nome de usuário é obrigatório');
      return;
    }

    setLoadingProfile(true);
    try {
      await api.put('/auth/profile', { username: username.trim() });
      await checkAuth();
      toast.success('Perfil atualizado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleToggleTwoFactor = async (enabled) => {
    setSavingTwoFactor(true);
    try {
      await api.put('/auth/profile', { two_factor_enabled: enabled });
      setTwoFactorEnabled(enabled);
      await checkAuth();
      toast.success(enabled ? 'Verificação em duas etapas ativada!' : 'Verificação em duas etapas desativada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSavingTwoFactor(false);
    }
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Digite um email válido');
      return;
    }

    setLoadingEmail(true);
    try {
      await api.post('/auth/change-email', { email: newEmail });
      setEmailStep('verify');
      toast.success('Código enviado para o novo email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar alteração');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    if (!emailCode || emailCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setLoadingEmail(true);
    try {
      await api.post('/auth/confirm-email-change', { user_id: user.id, code: emailCode });
      await checkAuth();
      setEmailStep('idle');
      setNewEmail('');
      setEmailCode('');
      toast.success('Email alterado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('A nova senha deve ter pelo menos 4 caracteres');
      return;
    }

    setLoadingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoadingPassword(false);
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { class: 'status-connected', label: 'Administrador', icon: Shield },
      master: { class: 'status-active', label: 'Master', icon: Shield },
      reseller: { class: 'status-pending', label: 'Revendedor', icon: User }
    };
    const c = config[role] || config.reseller;
    const Icon = c.icon;
    return (
      <Badge variant="outline" className={`${c.class} gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  return (
    <div data-testid="profile-page" className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">Meu Perfil</h1>
        <p className="text-muted-foreground text-sm mt-1">Gerencie suas informações pessoais</p>
      </div>

      {/* User Info Card */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
              <span className="font-heading font-bold text-2xl text-primary uppercase">
                {user?.username?.[0] || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-bold text-xl text-foreground">{user?.username}</h2>
              <p className="text-sm text-muted-foreground">{user?.email || 'Email não cadastrado'}</p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {getRoleBadge(user?.role)}
                {user?.panel_id && (
                  <Badge variant="outline" className="font-mono text-xs">
                    {user.panel_id}
                  </Badge>
                )}
                {(user?.role === 'master' || user?.role === 'admin') && (
                  <Badge variant="outline" className="status-pending gap-1">
                    <Coins className="w-3 h-3" />
                    {user?.role === 'admin' ? '∞ créditos' : `${user?.credits || 0} créditos`}
                  </Badge>
                )}
              </div>
              {user?.expires_at && user?.role !== 'admin' && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Expira em: {new Date(user.expires_at).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de configuração em grid lado a lado */}
      <div className="grid md:grid-cols-2 gap-6 items-start">
      {/* Email & Security */}
      <Card className="glass-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold text-lg text-foreground">Email e Segurança</h3>
          </div>
          
          {/* Current Email */}
          <div className="p-3 rounded-lg bg-muted/50 space-y-1">
            <p className="text-xs text-muted-foreground">Email atual</p>
            <p className="font-medium">{user?.email || 'Nenhum email cadastrado'}</p>
          </div>

          {/* Change Email */}
          {emailStep === 'idle' ? (
            <div className="space-y-2">
              <Label>Alterar Email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="novo@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="bg-muted/50"
                />
                <Button onClick={handleRequestEmailChange} disabled={loadingEmail || !newEmail}>
                  {loadingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Alterar'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Código de Verificação</Label>
              <p className="text-xs text-muted-foreground">Digite o código enviado para {newEmail}</p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-muted/50 font-mono text-center"
                />
                <Button onClick={handleConfirmEmailChange} disabled={loadingEmail}>
                  {loadingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                </Button>
                <Button variant="outline" onClick={() => { setEmailStep('idle'); setEmailCode(''); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Two Factor Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium">Verificação em Duas Etapas</p>
                <p className="text-xs text-muted-foreground">Receba um código por email ao fazer login</p>
              </div>
            </div>
            <Switch
              checked={twoFactorEnabled}
              onCheckedChange={handleToggleTwoFactor}
              disabled={savingTwoFactor || !user?.email}
            />
          </div>
          {!user?.email && (
            <p className="text-xs text-yellow-500">Cadastre um email para ativar a verificação em duas etapas</p>
          )}
        </CardContent>
      </Card>

      {/* Update Username */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold text-lg text-foreground">Alterar Nome de Usuário</h3>
          </div>
          
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Nome de Usuário</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Seu nome de usuário"
                className="bg-muted/50 border-border text-foreground"
              />
            </div>
            <Button
              type="submit"
              disabled={loadingProfile}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loadingProfile ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Salvar Alterações
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* WhatsApp Suporte - moved to App Config */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-500" />
              <div>
                <h3 className="font-heading font-semibold text-foreground">WhatsApp de Suporte</h3>
                <p className="text-xs text-muted-foreground">
                  {supportWhatsapp || 'Não configurado'} — Configure em App | Config
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/app-config'}>
              Configurar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold text-lg text-foreground">Alterar Senha</h3>
          </div>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Senha Atual</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Digite sua senha atual"
                  className="bg-muted/50 border-border text-foreground pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Nova Senha</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha"
                  className="bg-muted/50 border-border text-foreground pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme a nova senha"
                  className="bg-muted/50 border-border text-foreground pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loadingPassword}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loadingPassword ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Alterando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Alterar Senha
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      </div>

      {/* Configurações da Rede (master/admin) */}
      {isMasterOrAdmin && (
        <Card className="glass-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold text-lg text-foreground">Configurações da Rede</h3>
            </div>
            <p className="text-sm text-muted-foreground">Controle o que os revendedores da sua rede podem usar.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="pr-3">
                  <p className="text-sm font-medium text-foreground">Compra de conexão extra</p>
                  <p className="text-xs text-muted-foreground">Desative para bloquear a compra de conexões extras em toda a rede.</p>
                </div>
                <Switch checked={!netDisableConn} onCheckedChange={(v) => { setNetDisableConn(!v); saveNet({ disable_extra_connections: !v }); }} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Groups Section */}
      {isAdmin && (
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold text-lg text-foreground">Grupos com Link de Convite</h3>
              </div>
              <Button onClick={openGroupsModal} variant="outline">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver Todos os Grupos
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Visualize todos os grupos do sistema que possuem link de convite disponível.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Groups Modal */}
      <Dialog open={groupsModalOpen} onOpenChange={setGroupsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Grupos com Link de Convite ({groups.length})
            </DialogTitle>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar por nome ou dono..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              className="pl-10 pr-10 bg-muted/50"
            />
            {groupSearch && (
              <button
                onClick={() => setGroupSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Groups List */}
          <div className="flex-1 overflow-y-auto space-y-2 mt-4">
            {loadingGroups ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredGroups.length > 0 ? (
              filteredGroups.map((group) => (
                <div 
                  key={group.id} 
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50 hover:bg-muted/50 transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{group.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Badge variant="outline" className="text-[10px]">
                        {group.owner_username}
                      </Badge>
                      <span>•</span>
                      <span>{group.connection_name}</span>
                      {group.participants_count && (
                        <>
                          <span>•</span>
                          <span>{group.participants_count} membros</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => window.open(group.invite_link, '_blank')}
                    className="ml-2"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Entrar
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {groupSearch ? 'Nenhum grupo encontrado' : 'Nenhum grupo com link de convite'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
